<div class="glm-dialog">
  <button
    class="close-btn"
    mat-icon-button
    (click)="close()"
    aria-label="Close GLM 4.7 Info"
  >
    ×
  </button>

  <!-- Reasoning Level Control -->
  <div class="reasoning-control" *ngIf="supportsReasoning">
    <label for="reasoning-level">Reasoning Level:</label>
    <select 
      id="reasoning-level" 
      [(ngModel)]="selectedReasoningLevel"
      (change)="onReasoningLevelChange($any($event.target).value)"
      class="reasoning-select">
      <option value="auto">Auto</option>
      <option value="instant">Instant</option>
      <option value="thinking">Thinking</option>
      <option value="pro">Pro</option>
    </select>
    <span class="reasoning-description">
      Current: {{ selectedReasoningLevel | titlecase }} — applied to the next request
    </span>
  </div>

  <div class="header">
    <img src="assets/logos/zai.svg" alt="GLM 4.7 Logo" class="logo" />
    <h2>GLM 4.7</h2>
    <div class="sub">
      Bilingual, high-throughput reasoning model built for structured tasks, long-context reading, and tool-enabled agent workflows
    </div>
  </div>

  <div class="body">
    <p>
      <strong>GLM 4.7</strong> is a modern model in the <strong>GLM-4</strong> family from <strong>Zhipu AI</strong>,
      designed to behave like a dependable “work engine” for production systems.
      It aims to balance <strong>speed</strong>, <strong>reasoning depth</strong>, <strong>code competence</strong>,
      and <strong>format discipline</strong>—so it can operate reliably in real workflows instead of only performing well in casual chat.
    </p>

    <p>
      In practice, GLM 4.7 is most valuable when your prompts involve
      <strong>multiple constraints</strong>, <strong>step-by-step planning</strong>, <strong>long inputs</strong>
      (policies, tickets, transcripts, documentation), or when you need
      <strong>stable structured output</strong> for downstream automation (schemas, JSON, checklists, tool calls).
      It’s also a strong pick for bilingual environments where English and Chinese appear in the same conversation or document set.
    </p>

    <div class="optimal">
      <h3>Reasoning controls</h3>
      <ul>
        <li><strong>Auto:</strong> adapts depth based on complexity (best default for mixed workloads).</li>
        <li><strong>Instant:</strong> prioritizes speed for lightweight tasks (short answers, quick rewrites, simple extraction).</li>
        <li><strong>Thinking:</strong> stronger multi-step reasoning for analysis, planning, and troubleshooting.</li>
        <li><strong>Pro:</strong> maximum depth for high-stakes decisions, complex workflows, and long-context synthesis.</li>
      </ul>
    </div>

    <ul>
      <li>
        🌍 <strong>Bilingual + mixed-language fluency:</strong> Strong performance in <strong>English and Chinese</strong>,
        including translation, terminology consistency, and switching languages mid-context without losing meaning.
      </li>
      <li>
        🧠 <strong>Reasoning + decomposition:</strong> Good at breaking complex tasks into steps, explaining “why,”
        handling constraints, and producing decision-ready output (tradeoffs, options, recommendations).
      </li>
      <li>
        🧑‍💻 <strong>Coding + engineering assistance:</strong> Helps with debugging, refactoring, writing tests,
        documenting APIs, and producing “implementation-ready” plans (what to change, where, and why).
      </li>
      <li>
        📄 <strong>Long-context reading:</strong> Useful for reviewing large inputs such as policies, contracts,
        incident writeups, meeting transcripts, specs, and knowledge-base dumps—then summarizing with fidelity.
      </li>
      <li>
        🧾 <strong>Structured outputs &amp; schema discipline:</strong> Strong at returning predictable formatting:
        JSON-like objects, bullet schemas, tables, and extraction templates for automation and UI rendering.
      </li>
      <li>
        🧰 <strong>Agent-style workflows:</strong> Works well as the “planner” in pipelines:
        <strong>search → reason → execute → validate → summarize</strong>, with consistency across steps.
      </li>
      <li>
        🔐 <strong>Enterprise reliability:</strong> Tuned for steadier behavior (less format drift),
        clearer instruction adherence, and safer defaults for professional environments.
      </li>
    </ul>

    <div class="optimal">
      <h3>What GLM 4.7 is commonly used for</h3>
      <ul>
        <li>Enterprise copilots: support agents, operations assistants, onboarding, internal knowledge Q&amp;A</li>
        <li>Document intelligence: summarization, comparison, extraction, and compliance-style analysis</li>
        <li>Engineering workflows: debugging, refactoring, code review notes, architecture planning</li>
        <li>Tool-based automation: ticket routing, CRM/ERP helpers, workflow orchestration with structured outputs</li>
        <li>Executive clarity: decision memos, strategy summaries, meeting outcomes, and action plans</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>When GLM 4.7 is the best pick</h3>
      <ul>
        <li>🧠 You need reasoning beyond “fast chat” quality—especially with multiple constraints</li>
        <li>📄 You’re working with long inputs and need reliable synthesis, not shallow summarization</li>
        <li>🧾 Your app depends on structured output (schemas/JSON) that must stay consistent</li>
        <li>🧑‍💻 You want strong code help plus readable explanations for implementation</li>
        <li>🔧 You’re building agent workflows and need stable multi-step execution</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Recommended production usage pattern</h3>
      <ul>
        <li>Use smaller/cheaper models for drafts, quick rewrites, and low-risk tasks</li>
        <li>Escalate to <strong>GLM 4.7</strong> for planning, analysis, long-doc reasoning, or “final answer” passes</li>
        <li>For automation: require <strong>explicit schemas</strong> (keys, types, required fields, allowed values)</li>
        <li>Add validation: schema checks + rule checks (and retry with corrections when invalid)</li>
        <li>For high-stakes ops: run a second “verifier” pass (consistency + hallucination checks)</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Where GLM 4.7 tends to shine</h3>
      <ul>
        <li>🔎 <strong>Root-cause analysis:</strong> logs, errors, incidents, timeline reconstruction, “what changed?”</li>
        <li>🧠 <strong>Decision support:</strong> tradeoffs, constraints, best-option reasoning, risk analysis</li>
        <li>📚 <strong>Knowledge-heavy work:</strong> deep reading + synthesis across large text inputs</li>
        <li>🧑‍💻 <strong>Engineering copilots:</strong> code reasoning, patch suggestions, and system design support</li>
        <li>🧰 <strong>Agents:</strong> reliable structured plans + consistent step execution with tools</li>
      </ul>
    </div>
  </div>
</div>
